import { computed, ref } from 'vue'
import { createItemStore } from '~/utils/pinia/light-item-store'
import { usePickemApi } from '~/services/api'
import type { Tournament } from '../types'

// Количество архивных турниров в списке
export const TOURNAMENTS_COUNT_PER_PAGE = 10

// Источник турнира: `active` (GET /tournaments/active),
// `lastFinished` (GET /tournaments/last-finished) — fallback когда активного нет,
// `byId` (GET /tournaments/{id}) — для архивных или прямой ссылки.
export type TournamentSource =
  | { mode: 'active' }
  | { mode: 'lastFinished' }
  | { mode: 'byId'; id: number }

// Универсальный store: сама ручка выбирается во время fetchItem(source).
// Это позволяет одному компоненту Tournament.vue работать в трёх режимах
// (см. plan: pages/pickem/index.vue, pages/pickem/[id].vue, LastFinishedFallback.vue).
export const useTournamentStore = createItemStore(
  'pickem-tournament',
  {
    getItemRequest: async () => {
      const pickemApi = usePickemApi()

      return async (source: TournamentSource): Promise<Tournament | null> => {
        switch (source.mode) {
          case 'active':
            return pickemApi.getActiveTournament()
          case 'lastFinished':
            return pickemApi.getLastFinishedTournament()
          case 'byId':
            return pickemApi.getTournamentById(source.id)
        }
      }
    }
  },
  context => {
    const lastSource = ref<TournamentSource | null>(null)

    const fetchItem = async (source: TournamentSource) => {
      lastSource.value = source
      return context.fetchItem(source)
    }

    const tournament = computed(() => context.data.value)
    const isFinished = computed(() => tournament.value?.status === 'finished')
    const isActive = computed(() => tournament.value?.status === 'active')

    // Сортировка по position (бэк должен отдавать так, но страхуемся).
    const stages = computed(
      () =>
        tournament.value?.stages_summary
          ?.slice()
          .sort((a, b) => a.position - b.position) ?? []
    )

    // Краткая сводка по юзеру — для шапки и hero-карточки.
    const userSummary = computed(() => ({
      xp: tournament.value?.user_xp ?? 0,
      correctCount: tournament.value?.user_correct_count ?? 0
    }))

    return {
      tournament,
      stages,
      isFinished,
      isActive,
      userSummary,
      lastSource,
      fetchItem
    }
  }
)
